<template>
	<div ref="task" class="task-card flex items-stretch w-100" data-testid="task-card" tabindex="0" @click="onTaskClick" @keydown="onTaskClick">
		<div
			class="task-card__priority-bar shrink-0"
			:class="{
				'high-priority': priority === 1,
				'medium-priority': priority === 2,
				'low-priority': priority === 3
			}"
		/>
		<div class="task-card__inner grow">
			<div class="task-card__top flex items-center text-subtle">
				<CToolTip placement="bottom" :use-button="false" class="shrink-0">
					<span>{{ taskStatus }}</span>

					<template #button>
						<CSwatch :color="statusColor" />
					</template>
				</CToolTip>
				<div class="task-card__top-content flex items-center grow">
					<div :class="{ 'task-card__summary': !!taskSummary}" class="font-bold sz-3 truncate text-primary">
						{{ taskSummary }}
					</div>
					<div class="task-card__description truncate">
						{{ taskDescription }}
					</div>
					<div v-if="orderSummary" class="task-card__payment-status hidden items-center gap-3">
						<Inline svg="credit-card" />
						<span>{{ orderSummary }}</span>
					</div>
					<div class="task-card__due-date ml-auto shrink-0">
						{{ taskDueDate }}
					</div>
				</div>
			</div>
			<div class="task-card__bottom flex items-center">
				<div class="flex items-center gap-4" data-testid="task-view-link">
					<RouterLink :to="entryViewRoute" class="task-card__entry-view anchor--no-style text-subtle text-left truncate">
						{{ taskSource }}
					</RouterLink>
					<div v-if="orderSummary" class="task-card__payment-status flex items-center gap-3 text-subtle">
						<Inline svg="credit-card" />
						<span>{{ orderSummary }}</span>
					</div>
				</div>
				<CMultiButton
					v-if="actions.length > 1"
					:buttons="actions"
					:button-status="buttonStatus"
					class="task-card__actions ml-auto"
					@click="handleActionClick"
				/>
				<CStatusButton
					v-else-if="actions.length === 1"
					class="ml-auto task-card__action button button--no-style shrink-0"
					:status="buttonStatus"
					@click.native="handleActionClick(actions.at(0).value)"
				>
					{{ actions.at(0).displayValue }}
				</CStatusButton>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
	import type { FormEntryIndex } from '@cognitoforms/types/server-types/forms/model/form-entry-index';
	import { defineComponent, computed, ref, onMounted, onUnmounted, watch } from 'vue';
	import CSwatch from 'src/components/app/Swatch.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import CMultiButton from 'src/components/form-controls/MultiButton.vue';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import CStatusButton from 'src/components/interaction/StatusButton.vue';
	import { getFormId, getViewId } from './utils/use-task-utils';
	import type { DisplayAction } from './utils/use-task-actions';

	export default defineComponent({
		components: {
			CSwatch,
			CToolTip,
			CMultiButton,
			CStatusButton
		},
		props: {
			index: { type: Object as PropType<FormEntryIndex>, required: true },
			getEntry: { type: Function as PropType<(index: FormEntryIndex) => Promise<FormEntry>>, required: false, default: async () => Promise.resolve({} as FormEntry) },
			getActions: { type: Function as PropType<(index: FormEntryIndex) => Promise<DisplayAction[]>>, required: false, default: async () => Promise.resolve([]) },
			formatDate: { type: Function as PropType<(formId: string, date: Date) => Promise<string>>, required: false, default: async () => Promise.resolve(null) },
			taskSource: { type: String, required: true },
			entryViewRoute: { type: String, required: true },
			actionStatus: { type: String as PropType<ButtonStatus>, default: 'default' },
			staticDueDate: { type: Date, default: null },
			priority: { type: Number, default: null },
			isActionInProgress: { type: Boolean, default: false }
		},
		emits: ['click', 'perform-action', 'actions-loaded'],
		setup(props, { emit }) {
			const entry = ref(null);
			const handleIntersect = async (task) => {
				if (task.isIntersecting && !entry.value) {
					props.getEntry(props.index).then(loadedEntry => {
						entry.value = loadedEntry;
					});
				}
			};

			const taskDueDate = ref<string>(null);
			const updateTaskDueDate = async () => {
				if (props.staticDueDate)
					taskDueDate.value = await props.formatDate(getFormId(props.index), new Date(props.staticDueDate));
				else if (props.index.DueDate)
					taskDueDate.value = await props.formatDate(getFormId(props.index), new Date(props.index.DueDate));
				else
					taskDueDate.value = null;
			};

			watch(() => props.staticDueDate, async () => {
				await updateTaskDueDate();
			});

			const actions = ref([]);
			watch(() => entry.value, async (loadedEntry) => {
				if (!loadedEntry)
					return;

				actions.value = await props.getActions(props.index);

				const eventData = {
					viewId: getViewId(props.index),
					actions: actions.value,
					taskId: props.index.Id
				};
				emit('actions-loaded', eventData);
			});

			watch(() => props.index, async (updatedIndex) => {
				if (updatedIndex)
					await props.getEntry(updatedIndex).then(loadedEntry => {
						entry.value = loadedEntry;
					});

				await updateTaskDueDate();
			});

			const onTaskClick = (e) => {
				if (e.target.closest('button') || e.target.closest('a')) return;
				if (props.isActionInProgress) return;
				if (e.key === 'Enter' || e.key === ' ' || e.type === 'click') emit('click', props.index);
			};

			const showErrorIcon = ref(false);
			const buttonStatus = computed<ButtonStatus>(() => {
				if (showErrorIcon.value)
					return 'error';
				return props.actionStatus;
			});

			const handleActionClick = (actionName) => {
				if (props.isActionInProgress) {
					showErrorIcon.value = true;
					return;
				}

				const eventData = {
					actionName: actionName,
					index: props.index
				};
				emit('perform-action', eventData);
			};

			const taskStatus = computed<string>(() => {
				if (!entry.value)
					return '';

				return entry.value?.Entry?.Status;
			});

			const statusColor = computed<string>(() => {
				return entry.value?.Entry_Statuses?.find(status => status.Name === taskStatus.value)?.Color;
			});

			const taskSummary = computed<string>(() => {
				if (props.index.Summary)
					return props.index.Summary;
				else if (!props.index.Summary && !props.index.Description)
					return entry.value?.Entry?.Number;

				return '';
			});

			const taskDescription = computed<string>(() => {
				if (props.index.Description)
					return props.index.Description;

				return '';
			});

			const orderSummary = computed<string>(() => {
				return entry.value?.Order?.OrderSummary;
			});

			const observer = new IntersectionObserver(entries => {
				handleIntersect(entries[0]);
			});

			const task = ref<HTMLElement | null>(null);

			onMounted(async () => {
				setTimeout(() => {
					observer.observe(task.value);
				});

				await updateTaskDueDate();
			});

			onUnmounted(() => {
				observer.disconnect();
			});

			return {
				task,
				observer,
				onTaskClick,
				handleActionClick,
				taskStatus,
				statusColor,
				taskSummary,
				taskDescription,
				taskDueDate,
				orderSummary,
				actions,
				buttonStatus
			};
		}
	});
</script>

<style lang="scss">
.task-card {
	cursor: pointer;
	transition: background-color $speed__hovered;

	// :has is deprecated by Cognito Forms
	// stylelint-disable-next-line selector-pseudo-class-disallowed-list
	.admin--desktop &:hover:not(:has(a:hover, button:hover)) {
		background-color: var(--bg-hovered);
	}

	& + & {
		margin-top: $space__4;
	}

	.task-card {

		&__priority-bar {
			width: 4px;
			border: 1px solid var(--border-primary);
			border-right: none;

			&.high-priority {
				background-color: var(--bg-priority-high);
				border: none;
			}

			&.medium-priority {
				background-color: var(--bg-priority-medium);
				border: none;
			}

			&.low-priority {
				background-color: var(--bg-priority-low);
				border: none;
			}
		}
	}

	&__inner {
		border: 1px solid var(--border-primary);
		border-left: none;
		padding: 12px;
		padding-top: 8px;
	}

	&__top {

		.swatch {
			width: $size__icon--large;
			height: $size__icon--large;
			margin-right: $space__3;
		}
	}

	&__summary,
	&__description {
		margin-right: $space__4;
	}

	&__bottom {
		min-height: 27px;
		margin-top: $space__3;
		padding-left: calc($size__icon--large + $space__3);
		gap: $space__4;
	}

	&__entry-view {
		border-radius: $border-radius__text-button;
		margin-left: -$space__2;
		padding: $space__1 $space__2;
		transition: background-color $speed__hovered;

		&:hover {
			background-color: var(--bg-hovered);
			box-shadow: 0 0 0 1px var(--border-primary);
		}
	}

	&__actions {
		box-shadow: 0 0 0 2px var(--bg-primary);

		:root & .el-dropdown__caret-button {
			border-left: 1px solid var(--text-cta-text);
			border-image: unset;
		}
	}

	:root &__actions .el-button,
	&__action {
		min-height: 27px;
		background-color: var(--shade-4);
		padding: .4rem 0;
		color: var(--text-primary);
		font-family: $app-text-type;

		&:hover {
			background-color: var(--bg-hovered);
		}

		span {
			font-size: 12.5px;
		}
	}

	&__action {
		display: flex;
		box-shadow: 0 0 0 1px var(--bg-primary);
		font-weight: $weight__text--bold;

		& > span {
			display: flex;
			padding-right: .875rem;
			padding-left: .875rem;
		}
	}

	/* stylelint-disable-next-line scss/at-rule-no-unknown */
	@container task-group (max-width: 500px) {

		&__top {
			align-items: flex-start;

			&-content {
				flex-wrap: wrap;
				row-gap: $space__2;

				.task-card__summary {
					flex-basis: 100%;
					margin-bottom: $space__2;
				}

				.task-card__payment-status {
					display: flex;
					margin-right: $space__4;
				}

				.ml-auto {
					margin-left: unset;
				}
			}
		}

		&__bottom {

			.task-card__entry-view {
				font-size: $size__1;
				font-weight: $weight__text--bold;
			}

			.task-card__payment-status {
				display: none;
			}
		}
	}

	* {
		min-width: 0;
	}
}
</style>